import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';
import InteractiveLogo from '@/components/InteractiveLogo';
// Header uses the interactive icon-only mark. The full logotype (icon + wordmark)
// is preserved for footer, reports, PDFs and other large placements.

const Navigation = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { isInsurer } = useUserRole();
  const { t } = useTranslation();

  useEffect(() => {
    // Check for current session
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false);
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const baseNavItems = [
    { name: t('nav.pricing'), href: '/#pricing' },
    { name: t('nav.about'), href: '/about' },
  ];

  // Only show Insurance Company dashboard to insurers
  const navItems = isInsurer 
    ? [...baseNavItems, { name: t('nav.insuranceCompany'), href: '/dashboard/insurance-company' }]
    : baseNavItems;

  const isActive = (path: string) => {
    if (path.startsWith('/#')) {
      return location.pathname === '/' && location.hash === path.slice(1);
    }
    return location.pathname === path;
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('/#')) return;
    e.preventDefault();
    const id = href.slice(2);
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `/#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-[1000] backdrop-blur-md overflow-visible">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group" aria-label="Sentinel Crop">
            <InteractiveLogo size={48} />
            <span className="hidden md:inline font-heading text-[15px] font-medium uppercase tracking-[0.18em] text-neutral-900 transition-colors group-hover:text-emerald-700">
              Sentinel Crop
            </span>
          </Link>

          {/* Right cluster: nav links + CTA tightly grouped */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.href)
                    ? 'text-primary'
                    : 'text-gray-900'
                }`}
              >
                {item.name}
              </Link>
            ))}

            <LanguageSelector />

            {loading ? (
              <div className="w-28 h-9 bg-muted animate-pulse rounded-2xl" />
            ) : user ? (
              <Link to="/dashboard">
                <Button size="sm" className="bg-[#2FB46D] hover:bg-[#27975C] text-white px-4 py-2 rounded-2xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2FB46D] focus-visible:ring-offset-2">
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.dashboard')}
                </Button>
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-900 hover:text-primary px-3 py-2 rounded-2xl font-semibold">
                    {t('nav.signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#2FB46D] hover:bg-[#27975C] text-white px-4 py-2 rounded-2xl font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2FB46D] focus-visible:ring-offset-2">
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;